/* eslint-disable @typescript-eslint/no-explicit-any -- the Azure Maps SDK is loaded from the CDN without typings */
import type { MapSite } from '@/routes/map/SitesMapPage.types'

type RelationshipStyle = { marker: string; background: string; color: string; border: string }

const DEFAULT_RELATIONSHIP_STYLE: RelationshipStyle = {
  marker: 'marker-blue',
  background: '#eff6ff',
  color: '#1d4ed8',
  border: '#bfdbfe',
}

const RELATIONSHIP_STYLES: Record<string, RelationshipStyle> = {
  CLIENTE: DEFAULT_RELATIONSHIP_STYLE,
  PROSPECT: { marker: 'marker-green', background: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  'CONTATTO QUALIFICATO': { marker: 'marker-yellow', background: '#fffbeb', color: '#b45309', border: '#fde68a' },
  'CESSATA ATTIVITA': { marker: 'marker-darkblue', background: '#f8fafc', color: '#475569', border: '#cbd5e1' },
}

export const getRelationshipStyle = (relationshipType: string | null) =>
  RELATIONSHIP_STYLES[relationshipType?.toUpperCase().trim() ?? ''] ?? DEFAULT_RELATIONSHIP_STYLE

const PIN_IMAGE_EXPRESSION = [
  'match',
  ['get', 'relationshipType'],
  ...Object.entries(RELATIONSHIP_STYLES).flatMap(([type, style]) => [type, style.marker]),
  DEFAULT_RELATIONSHIP_STYLE.marker,
]

export const getPinIconOptions = (selectedSiteId: number | null) => ({
  image: PIN_IMAGE_EXPRESSION,
  allowOverlap: true,
  anchor: 'bottom',
  size: ['case', ['==', ['get', 'siteId'], selectedSiteId ?? -1], 1.35, 1.0],
})

export const toSiteFeature = (atlas: any, site: MapSite) =>
  new atlas.data.Feature(new atlas.data.Point([site.longitude, site.latitude]), {
    siteId: site.siteId,
    companyName: site.companyName,
    relationshipType: site.relationshipType?.toUpperCase().trim(),
    site,
  })

const zoomIntoCluster = (atlas: any, map: any, dataSource: any, clusterId: number) => {
  dataSource.getClusterLeaves(clusterId, 1000, 0).then((leaves: any[]) => {
    const positions = leaves.map((leaf) => (leaf.geometry ? leaf.geometry.coordinates : leaf.getCoordinates()))
    if (positions.length === 0) return

    const [first] = positions
    // Sites at the same position (e.g. the same building) cannot be separated by a bounding box.
    if (positions.every(([lon, lat]: number[]) => lon === first[0] && lat === first[1])) {
      dataSource.getClusterExpansionZoom(clusterId).then((zoom: number) => {
        map.setCamera({ center: first, zoom: Math.min(zoom, 14), type: 'ease', duration: 400 })
      })
      return
    }
    map.setCamera({
      bounds: atlas.data.BoundingBox.fromPositions(positions),
      padding: 120,
      type: 'ease',
      duration: 400,
    })
  })
}

/** Adds the clustered data source and its layers; returns what the page updates later. */
export const addSiteLayers = (atlas: any, map: any, onSelectSite: (site: MapSite) => void) => {
  const dataSource = new atlas.source.DataSource(null, { cluster: true, clusterRadius: 45, clusterMaxZoom: 14 })
  map.sources.add(dataSource)

  const clusterLayer = new atlas.layer.BubbleLayer(dataSource, null, {
    radius: ['step', ['get', 'point_count'], 18, 10, 24, 50, 30],
    color: ['step', ['get', 'point_count'], '#e46c6c', 10, '#d75656', 50, '#d81d1d'],
    strokeColor: '#ffffff',
    strokeWidth: 2,
    filter: ['has', 'point_count'],
  })
  const clusterLabelLayer = new atlas.layer.SymbolLayer(dataSource, null, {
    iconOptions: { image: 'none' },
    textOptions: {
      textField: ['get', 'point_count_abbreviated'],
      offset: [0, 0.4],
      color: '#ffffff',
      font: ['StandardFont-Bold'],
      size: 13,
    },
    filter: ['has', 'point_count'],
  })
  const pinLayer = new atlas.layer.SymbolLayer(dataSource, null, {
    filter: ['!', ['has', 'point_count']],
    iconOptions: getPinIconOptions(null),
  })
  const pinLabelLayer = new atlas.layer.SymbolLayer(dataSource, null, {
    minZoom: 12,
    filter: ['!', ['has', 'point_count']],
    iconOptions: { image: 'none' },
    textOptions: {
      textField: ['get', 'companyName'],
      offset: [0, 1.2],
      color: '#0f172a',
      font: ['StandardFont-Bold'],
      size: 11,
    },
  })
  map.layers.add([clusterLayer, clusterLabelLayer, pinLayer, pinLabelLayer])

  map.events.add('click', [clusterLayer, clusterLabelLayer], (event: any) => {
    const cluster = event.shapes?.[0]
    const clusterId = cluster?.properties?.cluster_id ?? cluster?.getProperties?.()?.cluster_id
    if (clusterId !== undefined) zoomIntoCluster(atlas, map, dataSource, clusterId)
  })
  map.events.add('click', [pinLayer, pinLabelLayer], (event: any) => {
    const site = event.shapes?.[0]?.getProperties()?.site
    if (site) onSelectSite(site)
  })

  return { dataSource, pinLayer, pinLabelLayer }
}
